import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const BLOCK_ROLES = ["network_admin", "studio_owner", "studio_manager"];
const nextDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return d.toLocaleDateString("en-CA");
};
const nice = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

export const SlotBookDialog = ({ target, user, onClose, onSaved }) => {
  const [jobs, setJobs] = useState(null);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [blockMode, setBlockMode] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const canBlock = BLOCK_ROLES.includes(user.role);

  useEffect(() => {
    if (!target) return;
    setJobs(null); setQ(""); setPick(""); setBlockMode(false); setNote("");
    api.get("/slot-chart/jobs", { params: { studio_id: target.studio.id, lane: target.lane } })
      .then(({ data }) => setJobs(data))
      .catch((e) => { setJobs([]); toast.error(apiError(e.response?.data?.detail)); });
  }, [target]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (jobs || []).filter((j) => !s || `${j.customer_name} ${j.phone} ${j.vehicle} ${j.services.join(" ")}`
      .toLowerCase().includes(s));
  }, [jobs, q]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/slot-chart/bookings", {
        studio_id: target.studio.id, date: target.date, lane: target.lane,
        slot: target.slot || null, ...(blockMode ? { note } : { job_id: pick }),
      });
      toast.success(blockMode ? "Slot blocked" : "Job placed in slot");
      onSaved();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const isPpf = target?.lane === "ppf";
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="slot-book-dialog">
        {target && (
          <>
            <DialogHeader>
              <DialogTitle>{isPpf ? "PPF slot" : "Sunfilm"} · {target.studio.name}
                {isPpf && target.studio.slots > 1 ? ` (Slot ${target.slot})` : ""}</DialogTitle>
              <DialogDescription data-testid="slot-book-dates">
                {isPpf ? `Blocks ${nice(target.date)} and ${nice(nextDay(target.date))}.` : `For ${nice(target.date)} (1 day).`}
              </DialogDescription>
            </DialogHeader>
            {canBlock && (
              <div className="flex gap-2">
                <Button size="sm" variant={blockMode ? "outline" : "default"} onClick={() => setBlockMode(false)} data-testid="slot-mode-job">Pick a job</Button>
                <Button size="sm" variant={blockMode ? "default" : "outline"} onClick={() => setBlockMode(true)} data-testid="slot-mode-block">Block without job</Button>
              </div>
            )}
            {blockMode ? (
              <div>
                <Label>Note (walk-in, holiday …)</Label>
                <Textarea data-testid="slot-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
              </div>
            ) : (
              <JobPicker jobs={jobs} shown={shown} q={q} setQ={setQ} pick={pick} setPick={setPick} lane={target.lane} />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="slot-book-cancel">Cancel</Button>
              <Button onClick={save} disabled={busy || (!blockMode && !pick)} data-testid="slot-book-submit">
                {busy ? "Saving…" : blockMode ? "Block slot" : "Place job"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const JobPicker = ({ jobs, shown, q, setQ, pick, setPick, lane }) => (
  <div className="space-y-2">
    <div className="relative">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input className="pl-8" placeholder="Search customer, phone, vehicle" value={q}
        onChange={(e) => setQ(e.target.value)} data-testid="slot-job-search" />
    </div>
    <div className="max-h-72 space-y-1.5 overflow-y-auto" data-testid="slot-job-list">
      {jobs === null && <p className="p-3 text-sm text-muted-foreground">Loading jobs…</p>}
      {jobs && !shown.length && (
        <p className="p-3 text-sm text-muted-foreground" data-testid="slot-job-empty">
          No open {lane === "ppf" ? "PPF" : "Sunfilm"} jobs for this studio. Create the job first, then place it here.
        </p>
      )}
      {shown.map((j) => (
        <button key={j.id} type="button" onClick={() => setPick(j.id)} data-testid={`slot-job-option-${j.id}`}
          className={`w-full rounded-md border p-2.5 text-left text-sm transition-colors duration-150 ${pick === j.id ? "border-crimson-500 bg-crimson-50" : "hover:bg-slate-50"}`}>
          <div className="flex justify-between gap-2">
            <span className="font-medium text-brand-ink">{j.customer_name || "—"}</span>
            <span className="text-xs text-muted-foreground">{j.status}</span>
          </div>
          <div className="text-xs text-muted-foreground">{[j.phone, j.vehicle].filter(Boolean).join(" · ")}</div>
          <div className="truncate text-xs text-muted-foreground">{j.services.join(", ") || "No services yet"}</div>
          {j.booked_on && <div className="text-xs text-amber-700">Already on chart: {j.booked_on} — will move</div>}
        </button>
      ))}
    </div>
  </div>
);

export const SunfilmDayDialog = ({ day, list, cap, canAdd, onAdd, onPick, onClose }) => (
  <Dialog open={!!day} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-md" data-testid="slot-sf-dialog">
      {day && (
        <>
          <DialogHeader>
            <DialogTitle>Sunfilm · {day.studio.name}</DialogTitle>
            <DialogDescription>{nice(day.date)} — {list.length} of {cap} booked</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            {list.map((b) => (
              <button key={b.id} type="button" onClick={() => onPick(b)} data-testid={`slot-sf-item-${b.id}`}
                className="w-full rounded-md border p-2.5 text-left text-sm hover:bg-slate-50">
                <span className="font-medium">{b.customer_name || b.note || "Blocked by studio"}</span>
                <span className="ml-2 text-xs text-muted-foreground">{b.vehicle}</span>
              </button>
            ))}
            {!list.length && <p className="text-sm text-muted-foreground">Nothing booked yet.</p>}
          </div>
          <DialogFooter>
            {canAdd && list.length < cap && (
              <Button onClick={onAdd} data-testid="slot-sf-add-btn">Add job</Button>
            )}
            <Button variant="outline" onClick={onClose} data-testid="slot-sf-close">Close</Button>
          </DialogFooter>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export const SlotInfoDialog = ({ booking, canRelease, onClose, onReleased }) => {
  const [confirm, setConfirm] = useState(false);
  useEffect(() => setConfirm(false), [booking]);
  const release = async () => {
    try {
      await api.delete(`/slot-chart/bookings/${booking.id}`);
      toast.success("Slot released");
      onReleased();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };
  const b = booking;
  return (
    <Dialog open={!!b} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="slot-info-dialog">
        {b && (
          <>
            <DialogHeader>
              <DialogTitle>{b.kind === "studio_block" ? "Blocked by studio" : "Booked"} · {b.studio_name}
                {b.lane === "sunfilm" ? " (Sunfilm)" : ` (PPF slot ${b.slot})`}</DialogTitle>
              <DialogDescription>{nice(b.dates[0])}{b.dates.length > 1 ? ` – ${nice(b.dates[b.dates.length - 1])}` : ""}</DialogDescription>
            </DialogHeader>
            <dl className="grid grid-cols-3 gap-y-2 text-sm" data-testid="slot-info-details">
              <Row k="Customer" v={b.customer_name} /><Row k="Phone" v={b.phone} />
              <Row k="Vehicle" v={b.vehicle} /><Row k="Services" v={(b.services || []).join(", ")} />
              <Row k="Note" v={b.note} /><Row k="Booked by" v={b.booked_by_name} />
            </dl>
            <DialogFooter>
              {b.job_id && (
                <Button variant="outline" asChild data-testid="slot-open-job-btn">
                  <Link to={`/jobs/${b.job_id}`}>Open job</Link>
                </Button>
              )}
              {canRelease && (confirm ? (
                <Button variant="destructive" onClick={release} data-testid="slot-release-confirm">Confirm release</Button>
              ) : (
                <Button variant="outline" className="text-red-600" onClick={() => setConfirm(true)} data-testid="slot-release-btn">
                  Release slot
                </Button>
              ))}
              <Button onClick={onClose} data-testid="slot-info-close">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ k, v }) => (
  <>
    <dt className="text-muted-foreground">{k}</dt>
    <dd className="col-span-2 text-brand-ink">{v || "—"}</dd>
  </>
);
